import { Database } from "@revelate/types";
import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  RevenueIntelligenceChatRecord,
  RevenueIntelligenceChatRegistrationValue,
} from "../components/RevenueIntelligenceChatContext";

type SignalJson = string | number | boolean | null | SignalJson[] | { [key: string]: SignalJson };

const REVENUE_INTELLIGENCE_SIGNAL_QUERY_TIMEOUT_MS = 8_000;

export async function runRevenueIntelligenceSignalQuery<T>(
  query: (signal: AbortSignal) => PromiseLike<T>,
  timeoutMs = REVENUE_INTELLIGENCE_SIGNAL_QUERY_TIMEOUT_MS,
) {
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort();
      reject(new DOMException("Signal query timed out.", "AbortError"));
    }, timeoutMs);
  });
  try {
    return await Promise.race([
      Promise.resolve(query(controller.signal)),
      timeout,
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export const REVENUE_INTELLIGENCE_SIGNALS_CHANGED_EVENT = "revenue-intelligence-signals-changed";

export type RevenueIntelligencePersistentSignal = {
  available_actions: SignalJson;
  category: string;
  company_id: number;
  confidence: number | null;
  contract_id: string;
  deal: {
    id: number;
    is_closed: boolean | null;
    is_lost: boolean | null;
    is_won: boolean | null;
    name: string | null;
  } | null;
  deal_id: number | null;
  detected_at: string;
  evidence: string;
  event_type: "risk_signal" | "buying_signal" | "analysis_signal";
  first_seen_at: string;
  id: string;
  last_seen_at: string;
  lifecycle_state: "new" | "active" | "acknowledged" | "snoozed" | "resolved" | "dismissed";
  management_implication: string | null;
  priority_dimensions: SignalJson;
  recommended_action: string | null;
  source_fingerprint: string;
  source_message_id: string | null;
  seen_at: string | null;
  snoozed_until: string | null;
  strength: number | null;
  title: string;
};

type SignalDatabase = {
  __InternalSupabase: Database["__InternalSupabase"];
  public: {
    Enums: Database["public"]["Enums"];
    Functions: Database["public"]["Functions"] & {
      list_revenue_intelligence_home_signals: {
        Args: {
          result_limit?: number;
          target_company_id: number;
        };
        Returns: Array<RevenueIntelligencePersistentSignalRpcRow>;
      };
      transition_revenue_intelligence_signal: {
        Args: {
          requested_action: "mark_seen" | "snooze";
          requested_snoozed_until?: string | null;
          target_signal_id: string;
        };
        Returns: Array<{
          id: string;
          lifecycle_state: string;
          seen_at: string | null;
          snoozed_until: string | null;
        }>;
      };
    };
    Tables: Database["public"]["Tables"] & {
      revenue_intelligence_signals: {
        Insert: never;
        Relationships: [];
        Row: RevenueIntelligencePersistentSignal;
        Update: never;
      };
    };
    Views: Database["public"]["Views"];
  };
};

type RevenueIntelligencePersistentSignalRpcRow = Omit<
  RevenueIntelligencePersistentSignal,
  "company_id" | "deal" | "deal_id" | "detected_at" | "first_seen_at" | "last_seen_at" | "seen_at" | "snoozed_until"
> & {
  company_id: number;
  deal_id: number | null;
  deal_is_closed: boolean | null;
  deal_is_lost: boolean | null;
  deal_is_won: boolean | null;
  deal_name: string | null;
  detected_at: string;
  first_seen_at: string;
  last_seen_at: string;
  seen_at: string | null;
  snoozed_until: string | null;
};

async function getSignalClient() {
  const { supabaseClient } = await import("@/lib/supabase/client");
  return supabaseClient as unknown as SupabaseClient<SignalDatabase>;
}

export function formatPersistentSignalPercentage(value: number | null) {
  return value === null ? "—" : `${Math.round(value * 100)}%`;
}

export function getPersistentSignalKindLabel(
  eventType: RevenueIntelligencePersistentSignal["event_type"],
) {
  if (eventType === "risk_signal") return "Risk signal";
  if (eventType === "buying_signal") return "Buying signal";
  return "Analysis signal";
}

export function getPersistentSignalCustomerLabel(
  signal: Pick<RevenueIntelligencePersistentSignal, "category" | "title">,
) {
  const category = signal.category.toLocaleLowerCase().replace(/\s+/gu, "_");
  if (category.includes("competitor")) return "Buyer is evaluating a competing option";
  if (category.includes("delay") || category.includes("timeline")) {
    return "Buyer indicates a possible delay";
  }
  if (category.includes("budget")) return "Buyer indicates a budget constraint";
  if (category.includes("low_priority") || category.includes("lowpriority")) {
    return "Buyer engagement suggests low priority";
  }
  if (category.includes("next_step") || category.includes("nextstep")) {
    return "Buyer has agreed a concrete next step";
  }
  return signal.title.replace(/^Buyer communication:\s*/iu, "Buyer communication indicates: ");
}

function asStringArray(value: SignalJson) {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function asPriorityDimensions(value: SignalJson) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return value as Record<string, SignalJson>;
}

type SignalPriorityRow = Pick<
  RevenueIntelligencePersistentSignal,
  "confidence" | "event_type" | "last_seen_at" | "lifecycle_state" | "priority_dimensions" | "strength"
>;

function getSignalPriorityFlag(signal: SignalPriorityRow, flag: string) {
  const dimensions = asPriorityDimensions(signal.priority_dimensions);
  return dimensions[flag] === true;
}

function compareSignals(left: SignalPriorityRow, right: SignalPriorityRow) {
  const lifecycle = (signal: SignalPriorityRow) => signal.lifecycle_state === "new" ? 0 : 1;
  return lifecycle(left) - lifecycle(right)
    || Number(getSignalPriorityFlag(right, "changed") || getSignalPriorityFlag(right, "conflicting"))
      - Number(getSignalPriorityFlag(left, "changed") || getSignalPriorityFlag(left, "conflicting"))
    || Number(right.strength ?? 0) - Number(left.strength ?? 0)
    || Number(right.confidence ?? 0) - Number(left.confidence ?? 0)
    || right.last_seen_at.localeCompare(left.last_seen_at);
}

export function prioritisePersistentSignalRows<T extends SignalPriorityRow>(
  signals: T[],
  limit = 8,
) {
  return signals.toSorted(compareSignals).slice(0, limit);
}

function buildSignalRecord(signal: RevenueIntelligencePersistentSignal): RevenueIntelligenceChatRecord {
  const dealLabel = signal.deal?.name ?? "Deal not specified";
  const signalKind = getPersistentSignalKindLabel(signal.event_type);
  const managementImplication = signal.management_implication
    ?? "No approved business implication is available.";
  const recommendedAction = signal.recommended_action
    ?? "No approved recommended action is available.";
  return {
    details: [
      dealLabel,
      signalKind,
      signal.confidence === null
        ? "Confidence not available"
        : `${formatPersistentSignalPercentage(signal.confidence)} confidence`,
    ],
    displayValues: {
      category: signal.category.replace(/_/gu, " "),
      confidence: formatPersistentSignalPercentage(signal.confidence),
      deal: dealLabel,
      detected_at: signal.detected_at,
      evidence: signal.evidence,
      management_implication: managementImplication,
      recommended_action: recommendedAction,
      signal: getPersistentSignalCustomerLabel(signal),
      signal_strength: formatPersistentSignalPercentage(signal.strength),
      signal_type: signalKind,
    },
    id: signal.id,
    label: signal.title,
    values: {
      category: signal.category,
      confidence: signal.confidence === null ? null : signal.confidence * 100,
      deal: dealLabel,
      detected_at: signal.detected_at,
      evidence: signal.evidence,
      management_implication: managementImplication,
      recommended_action: recommendedAction,
      signal: signal.title,
      signal_strength: signal.strength === null ? null : signal.strength * 100,
      signal_type: signal.event_type,
    },
  };
}

function isMissingHomeSignalsRpcError(error: unknown) {
  const maybeError = error as { code?: unknown; message?: unknown };
  return maybeError.code === "PGRST202"
    || maybeError.code === "42883"
    || (typeof maybeError.message === "string" && maybeError.message.includes("list_revenue_intelligence_home_signals"));
}

async function listPersistentRevenueIntelligenceSignalsFromTable({
  companyId,
  limit = 8,
}: {
  companyId: string;
  limit?: number;
}) {
  const now = new Date().toISOString();
  const relevantSince = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
  const signalClient = await getSignalClient();
  const { data, error } = await runRevenueIntelligenceSignalQuery(signal => (
    signalClient
      .from("revenue_intelligence_signals")
      .select("id,company_id,deal_id,contract_id,source_fingerprint,source_message_id,event_type,category,title,evidence,management_implication,recommended_action,strength,confidence,priority_dimensions,available_actions,lifecycle_state,first_seen_at,last_seen_at,detected_at,seen_at,snoozed_until,deal:deals(id,name,is_closed,is_won,is_lost)")
      .eq("company_id", Number(companyId))
      .or(`lifecycle_state.in.(new,active),and(lifecycle_state.eq.snoozed,snoozed_until.lte.${now})`)
      .gte("detected_at", relevantSince)
      .order("last_seen_at", { ascending: false })
      .limit(40)
      .abortSignal(signal)
  ));
  if (error) throw error;
  const visibleSignals = (data as unknown as RevenueIntelligencePersistentSignal[])
    .filter(signal => !(signal.deal && (signal.deal.is_closed || signal.deal.is_won || signal.deal.is_lost)))
    .map(signal => signal.lifecycle_state === "snoozed"
      ? { ...signal, lifecycle_state: "active" as const }
      : signal);
  return prioritisePersistentSignalRows(visibleSignals, limit);
}

export async function listPersistentRevenueIntelligenceSignals({
  companyId,
  limit = 8,
}: {
  companyId: string;
  limit?: number;
}) {
  const signalClient = await getSignalClient();
  const { data, error } = await runRevenueIntelligenceSignalQuery(signal => (
    signalClient
      .rpc("list_revenue_intelligence_home_signals", {
        result_limit: limit,
        target_company_id: Number(companyId),
      })
      .abortSignal(signal)
  ));
  if (error) {
    if (isMissingHomeSignalsRpcError(error)) {
      return listPersistentRevenueIntelligenceSignalsFromTable({ companyId, limit });
    }
    throw error;
  }
  return (data ?? []).map(mapSignalRpcRow);
}

export function buildPersistentRevenueIntelligenceSignalRegistration({
  companyId,
  signals,
}: {
  companyId: string;
  signals: RevenueIntelligencePersistentSignal[];
}): RevenueIntelligenceChatRegistrationValue {
  const records = signals.map(buildSignalRecord);
  return {
    accessPolicy: {
      allowedRoles: ["super_admin", "admin"],
      companyId,
      dataScope: "company",
      enforcement: "company_role_gate",
    },
    contractId: "revenue_intelligence.persistent_signals",
    datasets: [],
    facts: null,
    insights: signals.map(signal => {
      const dimensions = asPriorityDimensions(signal.priority_dimensions);
      return {
        body: signal.management_implication
          ?? "No approved management implication is available for this Signal yet.",
        caveat: signal.recommended_action
          ? undefined
          : "No approved recommended action is available for this Signal yet.",
        evidence: signal.evidence,
        insightId: `persistent-signal:${signal.id}`,
        label: signal.event_type === "risk_signal" ? "Email risk Signal" : "Email buying Signal",
        recommendedAction: signal.recommended_action ?? undefined,
        signal: {
          availableActions: asStringArray(signal.available_actions).filter((action): action is "explain" | "recommend" | "view_evidence" | "open_deal" | "mark_seen" | "snooze" => (
            ["explain", "recommend", "view_evidence", "open_deal", "mark_seen", "snooze"].includes(action)
          )),
          identity: {
            signalId: signal.id,
            sourceFingerprint: signal.source_fingerprint,
          },
          lifecycleState: signal.lifecycle_state,
          linkedRecordIds: signal.deal_id ? [String(signal.deal_id)] : [],
          priorityDimensions: {
            confidence: typeof dimensions.confidence === "number"
              ? dimensions.confidence
              : signal.confidence ?? undefined,
            novelty: signal.lifecycle_state === "new" ? "new" : "known",
            signalStrength: typeof dimensions.signal_strength === "number"
              ? dimensions.signal_strength
              : signal.strength ?? undefined,
          },
        },
        sourceFields: ["event_type", "category", "evidence", "strength", "confidence"],
        sourceHelper: "resolvePersistentRevenueIntelligenceSignals",
        sourceLabel: "Revenue Intelligence Signals",
        title: signal.title,
        topics: ["signals", "warnings", "email signals", signal.category],
      };
    }),
    metrics: [],
    recordSets: [{
      aliases: [
        "signals",
        "warnings",
        "alerts",
        "email signals",
        "varningar",
        "förändringar att uppmärksamma",
      ],
      caveats: [
        "The list uses existing semantic signal strength, confidence and novelty in explicit order. No combined priority score is calculated.",
        "A Signal without an approved management action remains evidence-only.",
      ],
      defaultSort: { direction: "desc", field: "signal_strength" },
      emptyMessage: "No active persistent Signals are available.",
      fields: [
        { aliases: ["signal"], field: "signal", filterable: true, label: "Signal", semanticRoles: ["dimension"], sortable: true, type: "category" },
        { aliases: ["deal", "affär"], field: "deal", filterable: true, label: "Deal", semanticRoles: ["identifier"], sortable: true, type: "category" },
        {
          aliases: ["type"],
          allowedFilterValues: [
            { aliases: ["risk signal", "risksignal"], label: "Risk signal", value: "risk_signal" },
            { aliases: ["buying signal", "köpsignal"], label: "Buying signal", value: "buying_signal" },
            { aliases: ["analysis signal", "analyssignal"], label: "Analysis signal", value: "analysis_signal" },
          ],
          field: "signal_type",
          filterable: true,
          label: "Signal type",
          sortable: true,
          type: "category",
        },
        { aliases: ["category", "kategori"], field: "category", filterable: true, label: "Category", sortable: true, type: "category" },
        { aliases: ["strength", "styrka"], field: "signal_strength", label: "Signal strength", sortable: true, type: "percentage" },
        { aliases: ["confidence", "säkerhet"], field: "confidence", label: "Confidence", sortable: true, type: "percentage" },
        { aliases: ["email excerpt", "exact quote", "evidence", "underlag", "mejlutdrag", "citat", "vad stod i mejlet"], field: "evidence", label: "Exact email excerpt", semanticRoles: ["evidence"], sortable: false, type: "category" },
        { aliases: ["business implication", "meaning", "impact", "betydelse", "konsekvens"], field: "management_implication", label: "Business implication", semanticRoles: ["explanation"], sortable: false, type: "category" },
        { aliases: ["recommended action", "next step", "what should we do", "rekommenderad åtgärd", "nästa steg", "vad bör vi göra"], field: "recommended_action", label: "Recommended action", semanticRoles: ["recommendation"], sortable: false, type: "category" },
        { aliases: ["detected", "found", "date", "upptäckt", "datum"], field: "detected_at", label: "Detected", sortable: true, type: "date" },
      ],
      label: "Prioritised Signals",
      rankingDescription: "New Signals first, then existing semantic signal strength, confidence and freshness. No combined score is used.",
      records,
      recordSetId: "revenue_intelligence.persistent_signals.active",
      responseTitle: "Signals — priority order",
      sourceFields: ["lifecycle_state", "event_type", "strength", "confidence", "last_seen_at"],
      sourceHelper: "resolvePersistentRevenueIntelligenceSignals",
      sourceLabel: "Revenue Intelligence Signals",
    }],
    scope: {
      filtersLabel: "New and active",
      pageLabel: "Revenue Intelligence Signals",
      periodLabel: "Current",
    },
    scopeKey: JSON.stringify({
      filtersLabel: "New and active",
      pageLabel: "Revenue Intelligence Signals",
      periodLabel: "Current",
    }),
    suggestedQuestions: ["Show the most important Signals", "Which email risks are new?"],
    topics: [
      "signals",
      "warnings",
      "alerts",
      "email signals",
      "risk signals",
      "buying signals",
      "risksignaler",
      "risksignalen",
      "köpsignaler",
      "köpsignalen",
      "varningar",
    ],
  };
}

function mapSignalRpcRow(
  row: RevenueIntelligencePersistentSignalRpcRow,
): RevenueIntelligencePersistentSignal {
  return {
    ...row,
    deal: row.deal_id
      ? {
        id: row.deal_id,
        is_closed: row.deal_is_closed,
        is_lost: row.deal_is_lost,
        is_won: row.deal_is_won,
        name: row.deal_name,
      }
      : null,
  };
}

export async function resolvePersistentRevenueIntelligenceSignals({
  companyId,
}: {
  companyId: string;
}): Promise<RevenueIntelligenceChatRegistrationValue[]> {
  const signals = await listPersistentRevenueIntelligenceSignals({ companyId });
  return [buildPersistentRevenueIntelligenceSignalRegistration({
    companyId,
    signals,
  })];
}

export async function getNewPersistentRevenueIntelligenceSignalCount({
  companyId,
}: {
  companyId: string;
}) {
  const signalClient = await getSignalClient();
  const { data, error } = await runRevenueIntelligenceSignalQuery(signal => (
    signalClient
      .rpc("list_revenue_intelligence_home_signals", {
        result_limit: 40,
        target_company_id: Number(companyId),
      })
      .abortSignal(signal)
  ));
  if (error) {
    if (isMissingHomeSignalsRpcError(error)) {
      const signals = await listPersistentRevenueIntelligenceSignalsFromTable({
        companyId,
        limit: 40,
      });
      return signals.filter(signal => signal.lifecycle_state === "new").length;
    }
    throw error;
  }
  return (data ?? []).filter(signal => signal.lifecycle_state === "new").length;
}

export async function transitionPersistentRevenueIntelligenceSignal({
  action,
  signalId,
  snoozedUntil = null,
}: {
  action: "mark_seen" | "snooze";
  signalId: string;
  snoozedUntil?: string | null;
}) {
  const signalClient = await getSignalClient();
  const { data, error } = await signalClient.rpc(
    "transition_revenue_intelligence_signal",
    {
      requested_action: action,
      requested_snoozed_until: snoozedUntil,
      target_signal_id: signalId,
    },
  );
  if (error) throw error;
  return data?.[0] ?? null;
}
